/**
 * serverErrorMessages.ts — WEB-754 (доделка аудита 22.08, п.2).
 *
 * Часть серверных отказов приходит машинным кодом в поле `code` плюс
 * английской технической строкой в `message`. Такую строку нельзя показывать
 * человеку: она ничего не объясняет и не говорит, что делать дальше.
 *
 * Первый случай — заморозка проекта по спорному платежу: сторож статуса
 * (`backend/src/projects/guards/status.guard.ts:55-60`) закрывает ВСЕ
 * проектные ручки замороженного проекта кодом `PROJECT_FROZEN` и строкой
 * «This project is frozen. Resolve billing to continue.». Раньше человек
 * видел именно её — во всплывающем уведомлении, по-английски.
 *
 * Соседний модуль `adminErrorMessages.ts` решает ту же задачу для админ-панели,
 * но ключом там служит САМО сообщение (у RolesGuard кода нет). Здесь ключ —
 * `code`, потому что структурированные отказы отдают его отдельным полем и
 * это стабильнее строки.
 *
 * Подключено в общий перехватчик ответов (`client.ts`), поэтому перевод
 * получают все вызывающие сразу, без правки по одной точке.
 */
import { defineDictionary, pickStrings } from '@/i18n/define';

/**
 * Текст для замороженного проекта. Называет причину (спор по платежу) и
 * следующий шаг: раздел «Баланс» намеренно НЕ закрыт сторожем статуса
 * (шапка status.guard.ts, §F13), поэтому оплатить из этого состояния можно —
 * совет рабочий, а не отписка.
 */
export const PROJECT_FROZEN_MESSAGE =
  'Проект приостановлен из-за спорного платежа: покупатель оспорил оплату в банке. ' +
  'Разделы проекта и его публичные страницы закрыты, пока вопрос не решён. ' +
  'Откройте раздел «Баланс», чтобы разобраться с оплатой, или напишите в поддержку.';

/**
 * Текст для проекта в режиме «только чтение» — ВТОРОЙ код того же сторожа
 * статуса (`status.guard.ts:65-69`, строка «This project is read-only.»).
 * Отдаётся только на запись: чтение разделов при этом статусе проходит.
 *
 * Причину текст НЕ называет намеренно. В отличие от заморозки, у которой
 * ратифицирована ровно одна причина (спорный платёж, P7), статус `readonly`
 * заведён как общий «project-wide read-only» (шапка `status.guard.ts`,
 * `project-status.enum.ts:5`) и ставится не одним источником. Назвать причину
 * значило бы утверждать то, чего мы не знаем, — на денежном пути это класс
 * WEB-1528. Поэтому текст честно описывает СОСТОЯНИЕ и даёт следующий шаг.
 */
export const PROJECT_READONLY_MESSAGE =
  'Проект переведён в режим «только чтение»: разделы открываются, но сохранить ' +
  'изменения нельзя. Проверьте состояние оплаты в разделе «Баланс» — если там всё ' +
  'в порядке, напишите в поддержку.';

/**
 * WEB-1505 (п.4): отказ по КОНКРЕТНОМУ ресурсу, а не по проекту целиком —
 * `shared/guards/assert-writable.ts` отвечает 403 с кодом `RESOURCE_READONLY`
 * и английской строкой про downgrade. Причина здесь названа прямо (в отличие
 * от `PROJECT_READONLY` выше): у неё ровно один источник — механизм понижения
 * тарифа (`billing/services/downgrade-enforcement.service.ts` замораживает всё
 * сверх лимита нового тарифа), так что утверждения на пустом месте нет.
 *
 * Сообщение нужно даже при погашенных кнопках: вкладку могли открыть до
 * понижения тарифа, и тогда запись уходит на сервер из уже устаревшего
 * состояния.
 */
export const RESOURCE_READONLY_MESSAGE =
  'Этот ресурс переведён в режим «только чтение»: он сверх лимита вашего ' +
  'текущего тарифа после его понижения. Смотреть и показывать в предпросмотре ' +
  'можно, сохранять и публиковать — нет. Вернитесь на прежний тариф, чтобы ' +
  'снова редактировать.';

/**
 * WEB-1556: сервер отказывает в создании проекта тому, у кого нет роли
 * владельца или администратора В ОРГАНИЗАЦИИ
 * (`backend/src/projects/services/projects.service.ts`, код
 * `ORG_ROLE_REQUIRED`). Раньше та же ситуация приходила как 404
 * «Organization not found» — человек видел поломку вместо отказа.
 *
 * Слова здесь ДОСЛОВНО те же, что сервер кладёт в `message`: один и тот же
 * отказ не должен звучать двумя формулировками — тому, кто повторил запрос
 * мимо кабинета, текст тоже виден. Совпадение держит сторож в
 * `__tests__/serverErrorMessages.test.ts` (читает исходник сервера).
 *
 * Текст нужен даже при погашенной кнопке: вкладку могли открыть до потери
 * прав, а запрос можно отправить и мимо кабинета.
 */
export const ORG_ROLE_REQUIRED_MESSAGE =
  'У вас нет прав создавать проекты в этой организации';

/**
 * WEB-1653: содержимое перевода изменилось, пока окно ручной правки было
 * открыто, — почти всегда его переписал догон, дописав недобранные строки за
 * наши деньги. Окно шлёт содержимое ЦЕЛИКОМ, поэтому сохранить его поверх
 * значило бы молча стереть свежую работу модели.
 *
 * Текст объясняет ровно это и снимает страх «мои правки пропали»: кабинет
 * перезагружает окно и накладывает правки человека на свежее содержимое, так
 * что терять их не приходится. Слова совпадают с серверными
 * (backend/src/translations/services/translation-content-version.util.ts,
 * TRANSLATION_CONTENT_CHANGED_MESSAGE) — совпадение держит сторож в
 * __tests__/serverErrorMessages.test.ts.
 */
export const TRANSLATION_CONTENT_CHANGED_MESSAGE =
  'Перевод изменился, пока окно правки было открыто: система догнала ' +
  'недостающие строки. Обновите окно, чтобы не потерять свежий перевод, — ' +
  'ваши правки при этом сохранятся.';

/**
 * WEB-1564: у одного человека может быть не больше десяти организаций —
 * потолок ПЛОСКИЙ, от тарифа не зависит (решение владельца 25.08: тарификация
 * у нас на уровне организации, у одного человека организации бывают на разных
 * тарифах, поэтому «какой у него тариф» в момент создания новой ответа не
 * имеет). Сервер отвечает 409 с кодом `ORG_LIMIT_REACHED`
 * (backend/src/shared/constants/org-limits.ts).
 *
 * Текст называет ВЫХОД, и выход этот рабочий ровно с WEB-1562: до неё
 * организацию нельзя было закрыть вовсе (мешали проекты), и упёршийся в
 * потолок оказался бы в тупике. Совет «повысьте тариф» здесь был бы прямым
 * обманом — потолок плоский, оплата его не двигает.
 *
 * Слова дословно совпадают с серверными — совпадение (вместе с числом) держит
 * сторож в `__tests__/serverErrorMessages.test.ts`, читающий исходник сервера.
 */
/**
 * Сколько организаций может ВЛАДЕТЬ один человек. Копия серверного числа
 * (`backend/src/shared/constants/org-limits.ts`) — иначе кабинету нечем
 * погасить дверь «Создать рабочее пространство» заранее. Совпадение с
 * сервером держит сторож в `__tests__/serverErrorMessages.test.ts`, читающий
 * исходник сервера: разъедутся — покраснеет здесь, а не у человека на экране.
 *
 * Это ВТОРАЯ линия, а не единственная: решение всё равно принимает сервер
 * (вкладку могли открыть до того, как потолок был достигнут, и запрос можно
 * отправить мимо кабинета).
 */
export const MAX_ORGS_PER_USER = 10;

export const ORG_LIMIT_REACHED_MESSAGE =
  'Достигнут предел: у одного человека может быть не больше 10 организаций. ' +
  'Чтобы создать новую, закройте ненужную — откройте её настройки, раздел «Опасная зона».';

/**
 * РЕВИЗИЯ ПАЧКИ 8, п.2 — ВТОРАЯ дверь того же потолка: передача владения.
 *
 * ЧТО ЛОМАЛОСЬ. Сервер отдавал оба отказа под одним кодом
 * `ORG_LIMIT_REACHED`, а кабинет переводит отказы по коду — и владелец,
 * пытавшийся ОТДАТЬ организацию, читал совет про создание новой. Ни причины
 * (предел у ПОЛУЧАТЕЛЯ, не у него), ни выхода он не узнавал, хотя сервер их
 * называл: кабинет их выбрасывал. Код разведён на сервере
 * (`ORG_LIMIT_REACHED_ON_TRANSFER`), здесь у него свой перевод.
 *
 * Слова дословно серверные — совпадение держит сторож в
 * `__tests__/serverErrorMessages.test.ts`, и он стоит на ОБА текста.
 */
export const ORG_LIMIT_REACHED_ON_TRANSFER_MESSAGE =
  'Передать нельзя: у получателя уже 10 организаций — это предел для одного человека. ' +
  'Передача пройдёт, когда он закроет ненужную — в её настройках, раздел «Опасная зона».';

/**
 * WEB-1874 (этап 2): те же тексты парой языков.
 *
 * ПОЧЕМУ СЛОВАРЬ ЛЕЖИТ ЗДЕСЬ, а не в `i18n/dictionaries/`. Русская половина
 * этих фраз — не «наш текст», а ЗЕРКАЛО серверных констант: сторож
 * `__tests__/serverErrorMessages.test.ts` читает исходник бэкенда и требует
 * дословного совпадения. Разнести зеркало и его сторожа по разным файлам
 * значило бы завести вторую копию правила ровно того класса, от которого
 * лечит WEB-1844. Поэтому здесь: русская половина ссылается на те же
 * константы, английская стоит рядом.
 *
 * Английские фразы — НЕ перевод серверной строки дословно, а то же самое
 * человеческим английским; сервер сегодня отвечает по-русски, и этап 4
 * (карточка сервера) научит его отдавать обе половины по коду.
 */
const serverErrorsStrings = defineDictionary(
  {
    noOrganization: 'You do not have an organization yet — create one first.',
    orgIdRequired:
      'You have several organizations — say which one the project goes to.',
    projectFrozen:
      'The project is suspended because of a payment dispute: the buyer disputed the charge with their bank. Its sections and public pages stay closed until that is settled. Open the "Balance" section to deal with the payment, or write to support.',
    projectReadonly:
      'The project is in read-only mode: sections open, but changes cannot be saved. Check the payment status in the "Balance" section — if everything is fine there, write to support.',
    resourceReadonly:
      'This resource is in read-only mode: it is over the limit of your current plan after the downgrade. You can view it and show it in preview, but not save or publish. Go back to the previous plan to edit it again.',
    orgRoleRequired: 'You are not allowed to create projects in this organization',
    orgLimitReached: `Limit reached: one person can have no more than ${MAX_ORGS_PER_USER} organizations. To create a new one, close an organization you no longer need — open its settings, the "Danger zone" section.`,
    orgLimitReachedOnTransfer: `Cannot transfer: the recipient already has ${MAX_ORGS_PER_USER} organizations, which is the limit for one person. The transfer will go through once they close one they no longer need — in its settings, the "Danger zone" section.`,
    translationContentChanged:
      'The translation changed while the editing window was open: the system filled in the missing strings. Reload the window so the fresh translation is not lost — your own edits will be kept.',
  },
  {
    // Ревизия пачки 2 (Б5): две соседние ветки того же resolveOrgId получили
    // коды тем же механизмом — тексты дословно серверные (сторож ниже).
    noOrganization: 'У вас пока нет организации — сначала создайте её.',
    orgIdRequired: 'У вас несколько организаций — укажите, в какой создать проект.',
    projectFrozen: PROJECT_FROZEN_MESSAGE,
    projectReadonly: PROJECT_READONLY_MESSAGE,
    resourceReadonly: RESOURCE_READONLY_MESSAGE,
    orgRoleRequired: ORG_ROLE_REQUIRED_MESSAGE,
    orgLimitReached: ORG_LIMIT_REACHED_MESSAGE,
    orgLimitReachedOnTransfer: ORG_LIMIT_REACHED_ON_TRANSFER_MESSAGE,
    translationContentChanged: TRANSLATION_CONTENT_CHANGED_MESSAGE,
  },
);

export { serverErrorsStrings };

/**
 * Машинный код отказа → ключ фразы (форма та же, что в i18n/dictionaries/errors.ts).
 *
 * WEB-1874 (часть 2): список ЭКСПОРТИРУЕТСЯ, потому что у него появился
 * второй конец — общий реестр серверных кодов
 * (`backend/src/shared/errors/human-error-codes.ts` → `i18n/dictionaries/
 * errors.ts`). Здешние фразы говорят ПОДРОБНЕЕ реестра и потому старше его;
 * сторож `utils/__tests__/localizeError.codes.web1874.test.ts` требует, чтобы
 * каждый такой код стоял в явном списке с причиной, — иначе второй текст
 * одного отказа заводится молча (WEB-1844).
 */
export const SERVER_ERROR_KEYS_BY_CODE: Readonly<
  Record<string, keyof typeof serverErrorsStrings.en>
> = {
  NO_ORGANIZATION: 'noOrganization',
  ORG_ID_REQUIRED: 'orgIdRequired',
  PROJECT_FROZEN: 'projectFrozen',
  PROJECT_READONLY: 'projectReadonly',
  RESOURCE_READONLY: 'resourceReadonly',
  ORG_ROLE_REQUIRED: 'orgRoleRequired',
  ORG_LIMIT_REACHED: 'orgLimitReached',
  ORG_LIMIT_REACHED_ON_TRANSFER: 'orgLimitReachedOnTransfer',
  TRANSLATION_CONTENT_CHANGED: 'translationContentChanged',
};

/**
 * Возвращает человеческий текст для известного машинного кода отказа НА ЯЗЫКЕ
 * КАБИНЕТА, иначе — исходное сообщение сервера без изменений.
 */
export function friendlyServerErrorMessage(
  code: unknown,
  message: string | undefined,
): string | undefined {
  if (typeof code === 'string') {
    // Поиск через hasOwnProperty — та же дисциплина, что в `curatedPhraseKey`
    // (`utils/localizeError.ts`): у обычного объекта в цепочке прототипов есть
    // `toString`, `constructor`, `valueOf`, и `SERVER_ERROR_KEYS_BY_CODE['toString']`
    // вернул бы ФУНКЦИЮ вместо ключа фразы — человек с таким кодом отказа
    // увидел бы пустоту вместо ответа сервера (ревизия пачки 15, R1-6).
    const key = Object.prototype.hasOwnProperty.call(SERVER_ERROR_KEYS_BY_CODE, code)
      ? SERVER_ERROR_KEYS_BY_CODE[code]
      : undefined;
    if (key) return pickStrings(serverErrorsStrings)[key];
  }
  return message;
}
